import { Select, Switch, TimePicker } from 'antd';
import { Field, Form } from 'formik';
import styled, { css } from 'styled-components';

const cardSurface = css`
  display: flex;
  padding: 24px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 6px 6px 20px rgba(4, 76, 123, 0.2);
`;

export const SkeletonWrapper = styled.div`
  ${cardSurface}
  height: 100px;
  align-items: center;
  margin: 0 46px;
`;

export const AddRuleButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  padding: 0 24px;
  border: none;
  border-radius: 32px;
  background: #d9ecfa;
  color: #113e57;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  svg {
    font-size: 14px;
  }

  &:hover {
    background: #cbe2f6;
  }
`;

export const RulesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 46px 46px 46px;
`;

export const NoRulesDiv = styled.div`
  ${cardSurface}
  gap: 24px;

  .no-rules-icon {
    width: 20px;
    height: 20px;
  }

  .no-rules {
    font-size: 15px;
    font-weight: 500;
    color: #0a0a0a;
  }
`;

export const RuleForm = styled(Form)`
  ${cardSurface}
  flex-direction: column;
  gap: 24px;

  &.collapsed {
    cursor: pointer;
    gap: 0;
  }
`;

const commonGridStyles = css`
  display: grid;
  grid-template-columns: minmax(0, 482px) minmax(0, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const RuleTopRow = styled.div`
  ${commonGridStyles}
  align-items: center;
`;

export const RuleMainInputBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FieldLabel = styled.label`
  margin: 0;
  color: #113e57;
  font-size: 14px;
  line-height: 1.8;
  font-weight: 700;
`;

const commonInputStyles = css`
  height: 56px;
  border: 1px solid #d9ecfa;
  border-radius: 16px;
  background: #ffffff;
`;

export const TextField = styled(Field)`
  ${commonInputStyles}
  width: 100%;
  padding: 0 16px;
  font-size: 14px;
  color: #0a0a0a;
  line-height: 1.8;
  outline: none;

  &:focus {
    border-color: #0c76b8;
    box-shadow: 0 0 0 2px rgba(12, 118, 184, 0.12);
  }

  &.has-error {
    border-color: #d14343;
  }

  &.has-error:focus {
    box-shadow: 0 0 0 2px rgba(209, 67, 67, 0.12);
  }
`;

export const SelectFieldWrapper = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 20px;
    top: 50%;
    width: 7px;
    height: 7px;
    border-right: 2px solid #0c76b8;
    border-bottom: 2px solid #0c76b8;
    transform: translateY(-65%) rotate(45deg);
    pointer-events: none;
  }
`;

export const SelectField = styled(TextField)`
  appearance: none;
  cursor: pointer;
  padding-right: 44px;
`;

export const DeviceMultiSelectWrapper = styled.div`
  width: 100%;
`;

export const MultiSelectField = styled(Select)`
  width: 100%;

  .ant-select-selector {
    min-height: 56px;
    border: 1px solid #d9ecfa !important;
    border-radius: 16px !important;
    box-shadow: none !important;
    padding: 8px 16px !important;
    align-items: center;

    .ant-select-selection-wrap {
      align-self: auto;
    }
  }

  &.ant-select-focused .ant-select-selector {
    border-color: #0c76b8 !important;
    box-shadow: 0 0 0 2px rgba(12, 118, 184, 0.12) !important;
  }

  .ant-select-selection-placeholder,
  .ant-select-selection-item,
  .ant-select-selection-search-input {
    font-size: 14px;
    color: #0a0a0a;
  }

  &.has-error .ant-select-selector {
    border-color: #d14343 !important;
  }

  &.has-error.ant-select-focused .ant-select-selector {
    box-shadow: 0 0 0 2px rgba(209, 67, 67, 0.12) !important;
  }
`;

export const DeviceDropdown = styled.div`
  width: 100%;
  min-width: 320px;
  padding: 8px;
`;

export const DeviceOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
  padding-bottom: 8px;
`;

export const DeviceOption = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 54px;
  padding: 0 12px;
  border-radius: 12px;
  color: #0c547e;
  font-size: 14px;
  line-height: 1.1;
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    margin: 0;
  }

  span {
    font-size: 14px;
    line-height: 1.4;
  }

  &.selected {
    background: #52a5de;
    color: #003252;
  }
`;

export const DeviceActionButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 28px;
  border-top: 1px solid #d9ecfa;
  padding-top: 10px;
`;

export const DeviceActionButton = styled.button.attrs({ type: 'button' })`
  border: none;
  background: transparent;
  color: #557d9d;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;

  &.apply {
    color: #3f7396;
  }
`;

export const ValidationText = styled.p`
  position: relative;
  margin: 6px 0 0;
  color: #d14343;
  font-size: 12px;
  line-height: 1.4;
`;

export const ToggleActions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  align-self: flex-start;

  @media (max-width: 1100px) {
    justify-content: flex-start;
    padding-bottom: 0;
  }
`;

export const ToggleItemsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

export const ToggleItem = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  user-select: none;
`;

export const ToggleSwitch = styled(Switch)`
  &.ant-switch {
    background-color: #9fb7c8;
  }

  &.ant-switch-checked {
    background-color: #4da4d9;

    &.has-error {
      background-color: #ff5f5f;
    }
  }

  &.ant-switch:hover:not(.ant-switch-disabled) {
    background-color: #8aaabb;
  }

  &.ant-switch-checked:hover:not(.ant-switch-disabled) {
    background-color: #3d9acc;

    &.has-error {
      background-color: #d14343;
    }
  }
`;

export const ToggleCaretButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: #113e57;
  cursor: pointer;

  /* caret glyph points UP — rotate 180deg so collapsed state shows a down arrow */
  transform: rotate(180deg);

  &:hover {
    background: #eef6fd;
  }

  &.expanded {
    /* expanded state: caret points up (no rotation) */
    transform: rotate(0deg);
  }
`;

export const TopSectionGrid = styled.div`
  ${commonGridStyles}
  align-items: end;
`;

export const FormBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.condition-device-block {
    padding-top: 33px;

    @media (max-width: 1100px) {
      padding-top: 0;
    }
  }

  &.compact-field {
    min-width: 204px;
  }
`;

export const ConditionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const InfoLine = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: #0a0a0a;
  font-size: 14px;
  line-height: 1.8;

  svg {
    font-size: 20px;
  }
`;

export const ConditionRow = styled(ConditionsSection)``;

export const ConditionMainRow = styled.div`
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr) 24px;
  gap: 16px;
  align-items: end;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const ConditionSecondaryRow = styled.div`
  display: flex;
  gap: 12px;
  padding-left: 266px;

  @media (max-width: 1100px) {
    padding-left: 0;
  }
`;

export const DeleteConditionButton = styled.button.attrs({ type: 'button' })`
  display: block;
  margin-bottom: 16px;
  border: none;
  background: transparent;
  cursor: not-allowed;

  svg {
    height: 16px;
    width: 16px;
    fill: #ff5f5f;
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &:not(:disabled):hover svg {
    fill: #d14343;
  }

  @media (max-width: 1100px) {
    margin: 0;
    justify-self: end;
  }
`;

export const SingleOperatorPill = styled.div`
  display: flex;
  align-items: center;
  height: 56px;
  width: 100%;
  padding: 0 16px;
  border: 1px solid #d9ecfa;
  border-radius: 16px;
  background: #ffffff;
  color: #0a0a0a;
  font-size: 14px;
  line-height: 1.8;
`;

export const NumberStepperWrapper = styled.div`
  ${commonInputStyles}
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 8px;

  &.has-error {
    border-color: #d14343;
  }

  &:focus-within {
    border-color: #0c76b8;
    box-shadow: 0 0 0 2px rgba(12, 118, 184, 0.12);
  }
`;

export const NumberField = styled(Field)`
  width: 48px;
  border: none;
  outline: none;
  text-align: center;
  color: #1c1c1c;
  font-size: 16px;
  line-height: 1;
  background: transparent;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }

  &[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
`;

export const NumberStepperButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid #1c1c1c;
  border-radius: 999px;
  background: #ffffff;
  color: #1c1c1c;
  font-size: 10px;
  cursor: pointer;
`;

export const NumberStepperUnit = styled.span`
  min-width: 0;
  margin-left: 8px;
  color: #0a0a0a;
  font-size: 14px;
  line-height: 1.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ConditionsActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const AddConditionButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: #d9ecfa;
  color: #113e57;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  height: 48px;

  span,
  svg {
    font-size: 14px;
  }

  &:hover {
    background: #cbe2f6;
  }
`;

export const SectionDivider = styled.hr`
  height: 1px;
  margin: 0;
  border: none;
  background: #dde7ee;
`;

export const ScheduleSection = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const DaysColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const DaysButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DayButton = styled.button.attrs({ type: 'button' })`
  width: 27px;
  height: 27px;
  padding: 0;
  border: 1px solid #d9ecfa;
  border-radius: 999px;
  background: #d9ecfa;
  color: #0c547e;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;

  &.has-error {
    border-color: #d14343;
  }

  &.active {
    border-color: #0c547e;
    background: #0c547e;
    color: #ffffff;
  }
`;

export const TimeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const TimeInputRow = styled.div`
  display: block;
  width: 100%;

  @media (max-width: 1100px) {
    display: block;
  }
`;

export const RuleTimePicker = styled(TimePicker)`
  &.ant-picker {
    position: relative;
    width: 100%;
    min-height: 56px;
    padding: 0 16px;
    border: 1px solid #d9ecfa;
    border-radius: 16px;
    box-shadow: none;
    background: #ffffff;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease,
      background-color 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      right: 20px;
      top: 50%;
      width: 7px;
      height: 7px;
      border-right: 2px solid #0c76b8;
      border-bottom: 2px solid #0c76b8;
      transform: translateY(-65%) rotate(45deg);
      pointer-events: none;
    }
  }

  &.ant-picker-focused {
    border-color: #0c76b8;
    box-shadow: 0 0 0 2px rgba(12, 118, 184, 0.12);
  }

  &.ant-picker.has-error {
    border-color: #d14343;
  }

  &.ant-picker.has-error.ant-picker-focused {
    box-shadow: 0 0 0 2px rgba(209, 67, 67, 0.12);
  }

  &.ant-picker.ant-picker-disabled {
    background: #f7fbfe;
    color: #7b97ab;
  }

  .ant-picker-input {
    flex: 1 1 auto;
    min-width: 0;
    padding-right: 16px;
  }

  .ant-picker-input > input {
    font-size: 14px;
    line-height: 1.8;
    color: #0a0a0a;
  }

  .ant-picker-input > input::placeholder {
    color: #6d8797;
  }

  .ant-picker-input > input[disabled] {
    color: #7b97ab;
    cursor: not-allowed;
  }

  .ant-picker-suffix {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
    margin-inline-start: auto;
    padding: 0 24px 0 16px;
    color: #0a0a0a;
    pointer-events: none;
  }

  .ant-picker-active-bar {
    display: none;
  }
`;

export const TimezoneText = styled.span`
  display: block;
  min-width: 0;
  max-width: 100%;
  color: #0a0a0a;
  font-size: 14px;
  line-height: 1.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const RequiredText = styled.span`
  color: #113e57;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.8;
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 42px;
    border-radius: 999px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }
  }

  @media (max-width: 1100px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

export const DeleteRuleButton = styled.button.attrs({ type: 'button' })`
  border: none;
  background: transparent;
  color: #ff5f5f;
  padding: 0;

  svg {
    width: 18px;
    height: 18px;
    fill: #ff5f5f;
  }

  &:hover {
    color: #d14343;
  }

  &:hover svg {
    fill: #d14343;
  }
`;

export const CancelButton = styled.button.attrs({ type: 'button' })`
  padding: 0 20px;
  border: 1px solid #0c76b8;
  background: #ffffff;
  color: #0c76b8;

  svg {
    width: 24px;
    height: 24px;
    fill: #0c76b8;
  }

  &:hover {
    border: 1px solid transparent;
    background: #cbe2f6;
    color: #003252;
  }

  &:hover svg {
    fill: #003252;
  }
`;

export const SaveButton = styled.button.attrs({ type: 'submit' })`
  padding: 0 22px;
  border: 1px solid transparent;
  background: #d9ecfa;
  color: #003252;

  svg {
    width: 24px;
    height: 24px;
    fill: #003252;
  }

  &:hover {
    background: #cbe2f6;
  }
`;

export const MessageToastContent = styled.p`
  font-weight: 400;
  white-space: nowrap;

  span {
    font-weight: 700;
  }
`;
